import http from '@/http';
import {
  AnnouncementFilterCriteria,
  AnnouncementSearchResponse,
  AnnouncementAdminResponse
} from '@/interfaces/admin/announcement';
import { AxiosProgressEvent, AxiosRequestConfig } from 'axios'

/**
 * Controller for Announcement API endpoints
 */
export default class AnnouncementController {
  private static readonly BASE_ADMIN_URL = '/admin/announcements';

  /**
   * Get announcements with pagination, sorting, and filtering
   *
   * @param criteria - Filter criteria for searching, sorting, and pagination
   * @returns Promise with paginated announcement data
   */
  static async getAnnouncements(
    criteria: AnnouncementFilterCriteria = {}
  ): Promise<AnnouncementSearchResponse> {
    try {
      // Set defaults if not provided
      const queryParams = {
        limit: criteria.limit || 100,
        offset: criteria.offset || 0,
        sort: criteria.sortField || 'title',
        order: criteria.sortDirection || 'asc',
        q: criteria.search || ''
      };

      const response = await http.get<AnnouncementSearchResponse>(
        `${this.BASE_ADMIN_URL}/search`,
        { params: queryParams }
      );

      return response.data;
    } catch (error) {
      console.error('Error fetching announcements:', error);
      throw error;
    }
  }

  /**
   * Get a single announcement by its ID
   *
   * @param id - The ID of the announcement to retrieve
   * @returns Promise with the announcement data
   */
  static async getAnnouncementById(id: number): Promise<AnnouncementAdminResponse> {
    if (!id) {
      throw new Error('No announcement id provided');
    }

    try {
      const response = await http.get<AnnouncementAdminResponse>(`${this.BASE_ADMIN_URL}/${id}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching announcement with ID ${id}:`, error);
      throw error;
    }
  }

  /**
   * Create a new announcement
   *
   * @param payload - The announcement data (FormData) to create
   * @param onUploadProgressCallback - Optional callback for upload progress (accepts ProgressEvent)
   * @returns Promise with the created announcement data
   */
  static async createAnnouncement(
    payload: FormData,
    onUploadProgressCallback?: (event: AxiosProgressEvent) => void
  ): Promise<AnnouncementAdminResponse> {
    try {
      const url = `${this.BASE_ADMIN_URL}/new`;

      const config: AxiosRequestConfig = {};
      if (onUploadProgressCallback) {
        config.onUploadProgress = onUploadProgressCallback;
      }

      const response = await http.post<AnnouncementAdminResponse>(url, payload, config); // Pass config to http call
      return response.data;
    } catch (error) {
      console.error('Error creating announcement:', error);
      throw error;
    }
  }

  /**
   * Update an existing announcement
   *
   * @param id - The ID of the announcement to update
   * @param payload - The announcement data (FormData) to update
   * @param onUploadProgressCallback - Optional callback for upload progress (accepts ProgressEvent)
   * @returns Promise with the updated announcement data
   */
  static async updateAnnouncement(
    id: number,
    payload: FormData,
    onUploadProgressCallback?: (event: AxiosProgressEvent) => void
  ): Promise<AnnouncementAdminResponse> {
    if (!id) {
      throw new Error('Announcement ID is required for update.');
    }

    const url = `${this.BASE_ADMIN_URL}/${id}`;

    try {
      // --- Axios Config Setup ---
      const config: AxiosRequestConfig = {};
      if (onUploadProgressCallback) {
        config.onUploadProgress = onUploadProgressCallback;
      }

      const response = await http.put<AnnouncementAdminResponse>(url, payload, config); // Pass config to http call
      return response.data;
    } catch (error) {
      console.error(`Error updating announcement with ID ${id}:`, error);
      throw error;
    }
  }

  /**
   * Delete multiple announcements by ID
   *
   * @param ids - Array of announcement IDs to delete
   * @returns Promise with deletion result
   */
  static async deleteAnnouncements(ids: number[]): Promise<void> {
    if (!ids.length) {
      throw new Error('No announcement IDs provided for deletion');
    }

    try {
      await http.delete(this.BASE_ADMIN_URL, { params: { ids } });
    } catch (error) {
      console.error('Error deleting announcements:', error);
      throw error;
    }
  }

  /**
   * Assigns all available organizations to a specific announcement.
   * @param id - The ID of the announcement.
   * @returns The updated announcement data.
   */
  static async assignAllOrganizations(id: number): Promise<AnnouncementAdminResponse> {
    const response = await http.put<AnnouncementAdminResponse>(
      `${this.BASE_ADMIN_URL}/${id}/organizations/assign-all`
    );
    return response.data;
  }

  /**
   * Removes all assigned organizations from a specific announcement.
   * @param id - The ID of the announcement.
   * @returns The updated announcement data.
   */
  static async removeAllOrganizations(id: number): Promise<AnnouncementAdminResponse> {
    const response = await http.delete<AnnouncementAdminResponse>(
      `${this.BASE_ADMIN_URL}/${id}/organizations`
    );
    return response.data;
  }
}
